import { MemberAllTimeStats, MemberValueStat } from '@kudos/http-client'
import { TFunction } from '@shared/types/types'
import type { useTranslatedField } from '@shared/utils/useTranslatedField'
import { TimelineDirection } from '../../types'

export type TranslatedFieldGetter = ReturnType<typeof useTranslatedField>

/** A single metric in the row above the feed. */
export type RecognitionSummaryTile = {
  id: string
  label: string
  /** Counts are localized where they are rendered; a quality name is already translated. */
  value: number | string
}

/** A quality name paired with the number of times the member received it. */
export type TopQuality = {
  name: string
  count: number
}

const isBetterTopQuality = (candidate: TopQuality, current: TopQuality): boolean =>
  candidate.count > current.count ||
  (candidate.count === current.count && candidate.name.localeCompare(current.name) < 0)

/**
 * The one quality a member has been recognized for most, across every value.
 * The API orders qualities by descending count within each value but not across
 * them, so the winner has to be picked out of the flattened list.
 *
 * Ties break alphabetically, on the translated name, rather than falling to the
 * order the API happens to return. Early on a member's qualities all sit at a
 * count of one, so a tie decides the tile outright; the API breaks its own ties
 * on the untranslated name, which would read as unsorted to anyone else.
 *
 * The PRD asks for the most recently received quality first, which the endpoint
 * cannot answer — it returns counts only, with no date to order by.
 */
export const findTopQuality = (
  values: MemberValueStat[],
  getTranslatedField: TranslatedFieldGetter,
): TopQuality | undefined =>
  values
    .flatMap(value => value.qualities)
    .map(quality => ({ name: getTranslatedField(quality, 'name'), count: quality.count }))
    .reduce<TopQuality | undefined>(
      (top, quality) => (!top || isBetterTopQuality(quality, top) ? quality : top),
      undefined,
    )

/**
 * The metrics that summarise a member's recognition, for whichever half of the
 * timeline is on screen. Counts are all-time and deliberately unaffected by the
 * feed's type filter, so they hold still while the feed below them changes.
 *
 * A metric with nothing to report is left out entirely rather than shown as a
 * zero, per the epic.
 */
export const getRecognitionSummaryTiles = (
  direction: TimelineDirection,
  stats: MemberAllTimeStats | undefined,
  topQuality: TopQuality | undefined,
  t: TFunction,
): RecognitionSummaryTile[] => {
  // The quality stats are shared with the About tab, so they can already be
  // cached when the counts are still in flight. Treating the absent counts as
  // zeroes would leave the row holding a quality name on its own.
  if (!stats) {
    return []
  }

  const {
    recognitionsReceived,
    recognitionsSent,
    awardsReceived,
    awardsSent,
    membersAwardedOrRecognized,
  } = stats

  const tiles: RecognitionSummaryTile[] =
    direction === TimelineDirection.GIVEN
      ? [
          {
            id: 'recognitionSent',
            label: t('Recognition sent'),
            // Awards count towards what a member sent: unlike the received side,
            // there is no separate awards tile for them to land in.
            value: recognitionsSent + awardsSent,
          },
          {
            id: 'colleaguesRecognized',
            label: t('Colleagues recognized'),
            // Distinct recipients, so recognizing the same colleague twice
            // counts once — the count is the endpoint's, not derived here.
            value: membersAwardedOrRecognized,
          },
        ]
      : [
          {
            id: 'recognitionReceived',
            label: t('Recognition received'),
            value: recognitionsReceived,
          },
          {
            id: 'mostRecognizedFor',
            label: t('Most recognized for'),
            // A member with no recognition has no quality to name, so the tile
            // drops out with the empty counts beside it.
            value: topQuality?.name ?? 0,
          },
          {
            id: 'awardsEarned',
            label: t('Awards earned'),
            value: awardsReceived,
          },
        ]

  // A zero count, or a quality left unnamed in the tenant's language, both mean
  // there is nothing to report.
  return tiles.filter(({ value }) => Boolean(value))
}
